import { useRouter } from "next/router";
import useSWR from "swr";

import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import { Tab, Tabs } from "components/Tabs";
import useAuthentication from "hooks/useAuthentication";
import { useGlobalState } from "hooks/useGlobalState";
import { baseFetcher } from "utils/baseFetcher";

import DashboardPageInsightList from "../DashboardPageInsightList";

import FeedSettingsModal from "./FeedSettingsModal";
import SignUpPage from "./SignUpPage";

type DashboardPageMyFeedProps = {};

const DashboardPageMyFeed = ({}: DashboardPageMyFeedProps) => {
  const { authenticated, account: loggedInAccount } = useAuthentication();

  const router = useRouter();

  const sort = router.query?.sort as string;
  const filter = router.query?.filter as string;

  const topContentVerticalId = loggedInAccount?.topContentVerticalIds?.[0];
  const topAuthorId = loggedInAccount?.topAuthorIds?.[0];
  const topSector = loggedInAccount?.topSectors?.[0];

  const { data: topContentVertical } = useSWR(
    topContentVerticalId
      ? `/api/v2/content-verticals/${topContentVerticalId}?no-view-count=true`
      : null,
    baseFetcher
  );
  const { data: topAuthor } = useSWR(
    topAuthorId
      ? `/api/v2/accounts/${topAuthorId}?no-view-count=true&fields[accounts]=name`
      : null,
    baseFetcher
  );

  const [showFeedSettings, setShowFeedSettings] = useGlobalState(
    "showFeedSettingsModal",
    false
  );

  const showSparkleIcon =
    !loggedInAccount?.isPreviewPass ||
    topAuthor ||
    topContentVertical ||
    topSector;

  if (!authenticated) {
    return <SignUpPage />;
  }

  return (
    <>
      <Tabs className="pt-[60px] md:overflow-x-auto no-scrollbar">
        {showSparkleIcon && (
          <div className="mr-[10px] flex items-center">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="https://assets.smartkarma.com/icons/sparkle.svg"
              alt="Quiddity Northbound Monitor"
              width={20}
              height={22}
              className="pb-[2px]"
            />
          </div>
        )}
        <Tab
          href={`/dashboard?collection=my-reading-list&sort=${sort}`}
          active={!filter}
          className="md:max-w-none md:min-w-min md:overflow-visible bg-[#0276be19]"
          activeClassName="bg-[#0276be]"
          nonShallow
        >
          All
        </Tab>
        {!loggedInAccount?.isPreviewPass && (
          <Tab
            href={`/dashboard?collection=my-reading-list&filter=unread&sort=${sort}`}
            active={filter === "unread"}
            className="md:max-w-none md:min-w-min md:overflow-visible bg-[#0276be19]"
            activeClassName="bg-[#0276be]"
            nonShallow
          >
            Unread
          </Tab>
        )}
        {topAuthor && (
          <Tab
            href={`/dashboard?collection=my-reading-list&filter=top-author&sort=${sort}`}
            active={filter === "top-author"}
            className="md:max-w-none md:min-w-min md:overflow-visible bg-[#0276be19]"
            activeClassName="bg-[#0276be]"
            nonShallow
          >
            {topAuthor.name}
          </Tab>
        )}
        {topContentVertical && (
          <Tab
            href={`/dashboard?collection=my-reading-list&filter=top-vertical&sort=${sort}`}
            active={filter === "top-vertical"}
            className="md:max-w-none md:min-w-min md:overflow-visible bg-[#0276be19]"
            activeClassName="bg-[#0276be]"
            nonShallow
          >
            {topContentVertical.name}
          </Tab>
        )}
        {topSector && (
          <Tab
            href={`/dashboard?collection=my-reading-list&filter=top-sector&sort=${sort}`}
            active={filter === "top-sector"}
            className="md:max-w-none md:min-w-min md:overflow-visible bg-[#0276be19]"
            activeClassName="bg-[#0276be]"
            nonShallow
          >
            {topSector}
          </Tab>
        )}
      </Tabs>
      <CustomCard className={twMerge("p-0", "mt-0 md:mt-[15px]")}>
        <DashboardPageInsightList
          options={{
            filter: {
              collection: "my-reading-list",
              unread: filter === "unread",
              contentVerticalId:
                filter === "top-vertical" ? topContentVerticalId : undefined,
              accountId: filter === "top-author" ? topAuthorId : undefined,
              sector: filter === "top-sector" ? topSector : undefined,
            },
            showSorter: true,
            showTopPicksToggle: true,
            showMoreOptions: true,
            showSearch: true,
            showSuggestedCard: !filter,
          }}
        />
      </CustomCard>
      {showFeedSettings && (
        <FeedSettingsModal onClose={() => setShowFeedSettings(false)} />
      )}
    </>
  );
};

export default DashboardPageMyFeed;
